import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MappedDrive, NetworkAdapter, NetworkRegisteredIp, Printer } from '../../../../../Models/avyna/agent-device.model';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { AvTableColumn, AvTableDialogConfig, AvTableLinkCell, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';

@Component({
  selector: 'app-network',
  standalone: true,
  imports: [AvContentContainerComponent, AvTableCard],
  templateUrl: './network.component.html',
  styleUrls: ['./network.component.scss'],
})
export class NetworkComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly adapterColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.network.columns.name', sortable: true },
    { key: 'type', label: 'app.device.network.columns.type', sortable: true },
    { key: 'status', label: 'app.device.network.columns.status', sortable: true },
    { key: 'details', label: 'app.device.network.columns.details', type: 'link' },
  ];

  readonly ipAddressColumns: AvTableColumn[] = [
    { key: 'ipAddress', label: 'app.device.network.columns.ip_address', sortable: true },
    { key: 'lastRegistration', label: 'app.device.network.columns.last_registration', sortable: true },
  ];

  readonly printerColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.network.columns.name', sortable: true },
    { key: 'driver', label: 'app.device.network.columns.driver', sortable: true },
    { key: 'printStatus', label: 'app.device.network.columns.print_status', sortable: true },
  ];

  readonly sharedDriveColumns: AvTableColumn[] = [
    { key: 'letter', label: 'app.device.network.columns.letter', sortable: true },
    { key: 'shareName', label: 'app.device.network.columns.share_name', sortable: true },
    { key: 'available', label: 'app.device.network.columns.available', type: 'bool', sortable: true },
    { key: 'connection', label: 'app.device.network.columns.connection', type: 'bool', sortable: true },
    { key: 'details', label: 'app.device.network.columns.details', type: 'link' },
  ];

  readonly adapterRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const adapters = device?.agent?.network?.adapters;
    if (!Array.isArray(adapters)) return [];

    return adapters.map((adapter: NetworkAdapter) => ({
      name: this.fallbackString(adapter?.name),
      type: this.fallbackString(adapter?.type),
      status: this.fallbackString(adapter?.status),
      details: this.detailsLink(this.buildAdapterDialog(adapter)),
    }));
  });

  readonly ipAddressRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const addresses = device?.agent?.network?.ip_addresses;
    if (!Array.isArray(addresses)) return [];

    return addresses.map((address: NetworkRegisteredIp) => ({
      ipAddress: this.fallbackString(address?.ip_address),
      lastRegistration: this.fallbackString(address?.last_registration),
    }));
  });

  readonly printerRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const printers = device?.agent?.printers;
    if (!Array.isArray(printers)) return [];

    return printers.map((printer: Printer) => ({
      name: this.fallbackString(printer?.name),
      driver: this.fallbackString(printer?.driver),
      printStatus: this.fallbackString(printer?.print_saver_status),
    }));
  });

  readonly sharedDriveRows = computed<AvTableRow[]>(() => {
    const device = this.device();
    const drives = device?.agent?.mapped_drives;
    if (!Array.isArray(drives)) return [];

    return drives.map((drive: MappedDrive) => ({
      letter: this.fallbackString(drive?.letter),
      shareName: this.fallbackString(drive?.share_name),
      available: typeof drive?.is_available === 'boolean' ? drive.is_available : null,
      connection: this.normalizeConnection(drive?.connection_status),
      details: this.detailsLink(this.buildSharedDriveDialog(drive)),
    }));
  });

  private detailsLink(dialog: AvTableDialogConfig): AvTableLinkCell {
    return {
      icon: 'more_horiz',
      ariaLabel: 'app.device.network.columns.details',
      dialog,
    };
  }

  private buildAdapterDialog(adapter: NetworkAdapter): AvTableDialogConfig {
    return {
      titleKey: 'app.device.network.dialog.adapter_details.title',
      entries: [
        { labelKey: 'app.device.network.columns.name', value: adapter?.name },
        { labelKey: 'app.device.network.columns.type', value: adapter?.type },
        { labelKey: 'app.device.network.columns.status', value: adapter?.status },
        { labelKey: 'app.device.network.details.mac_address', value: adapter?.mac_address },
        { labelKey: 'app.device.network.details.ip_addresses', value: adapter?.ip_addresses },
      ],
    };
  }

  private buildSharedDriveDialog(drive: MappedDrive): AvTableDialogConfig {
    return {
      titleKey: 'app.device.network.dialog.shared_drive_details.title',
      entries: [
        { labelKey: 'app.device.network.columns.letter', value: drive?.letter },
        { labelKey: 'app.device.network.details.user_sid', value: drive?.user_sid },
        { labelKey: 'app.device.network.details.last_used', value: drive?.last_used },
        { labelKey: 'app.device.network.details.user_name', value: drive?.user_name },
        { labelKey: 'app.device.network.details.drive_type', value: drive?.drive_type },
        { labelKey: 'app.device.network.details.persistent', value: drive?.persistent },
        { labelKey: 'app.device.network.details.share_name', value: drive?.share_name },
        { labelKey: 'app.device.network.details.remote_path', value: drive?.remote_path },
        { labelKey: 'app.device.network.details.server_name', value: drive?.server_name },
        { labelKey: 'app.device.network.columns.available', value: drive?.is_available },
        { labelKey: 'app.device.network.details.source_method', value: drive?.source_method },
        { labelKey: 'app.device.network.details.connection_status', value: drive?.connection_status },
      ],
    };
  }

  private fallbackString(value?: string | null): string {
    if (value && value.trim().length > 0) return value;
    return this.translate.instant('shared.general.labels.na');
  }

  private normalizeConnection(value: unknown): boolean | null {
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string') return null;
    const normalized = value.trim().toLowerCase();
    if (['connected', 'online', 'true', 'yes'].includes(normalized)) return true;
    if (['disconnected', 'offline', 'false', 'no'].includes(normalized)) return false;
    return null;
  }

}
